import { Box, Grid, Stack, Tooltip, Typography } from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import { formatMoney } from 'lib/functions/utils';
import { PayoutDistribution } from 'lib/types/models';
import React from 'react';

type PayoutPlanDetailsProps = {
  selectedPayoutPlan: PayoutDistribution[];
  handleOpenCreateAvailableDialog: () => void;
  handleOpenEditPayoutPlan: () => void;
  onEditing: boolean;
};

const MAX_LENGTH = 20;

const TruncatedText = ({
  text,
  maxLength = MAX_LENGTH,
}: {
  text: string | number;
  maxLength?: number;
}) => {
  const textString = String(text);
  const shouldTruncate = textString.length > maxLength;
  const displayText = shouldTruncate
    ? `${textString.substring(0, maxLength)}...`
    : textString;

  if (shouldTruncate) {
    return (
      <Tooltip title={textString}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayText}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Typography variant="body2" fontWeight={500}>
      {displayText}
    </Typography>
  );
};

const PayoutPlanDetails = ({
  selectedPayoutPlan,
  handleOpenCreateAvailableDialog,
  handleOpenEditPayoutPlan,
  onEditing,
}: PayoutPlanDetailsProps) => {
  return (
    <div>
      <Box
        sx={{
          backgroundColor: 'grey.50',
          p: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200',
          mb: 2,
        }}
      >
        {selectedPayoutPlan.length !== 0 && (
          <Grid container>
            <Grid item xs={4}>
              <Typography variant="body2" fontWeight={600}>
                Banco
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2" fontWeight={600}>
                Cuenta
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" fontWeight={600}>
                Tipo
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" fontWeight={600}>
                Monto asignado
              </Typography>
            </Grid>

            {selectedPayoutPlan.map((p, index) => (
              <>
                <Grid item xs={4} key={`${index}-name`}>
                  <TruncatedText text={p.bankAccount.bankName ?? ''} />
                </Grid>
                <Grid item xs={2} key={`${index}-account`}>
                  <TruncatedText text={p.bankAccount.accountNumber ?? ''} />
                </Grid>
                <Grid item xs={3} key={`${index}-type`}>
                  <TruncatedText text={p.bankAccount.type ?? ''} />
                </Grid>
                <Grid item xs={3} key={`${index}-amount`}>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {formatMoney(p.amount)}
                  </Typography>
                </Grid>
              </>
            ))}
          </Grid>
        )}

        {selectedPayoutPlan.length === 0 && (
          <Grid item xs={12}>
            <Stack direction={'row'} justifyContent={'center'}>
              <Typography variant="body2" fontWeight={500} fontStyle={'italic'}>
                No se ha seleccionado ninguna cuenta de banco
              </Typography>
            </Stack>
          </Grid>
        )}
      </Box>

      {onEditing && (
        <Stack
          direction={'row'}
          alignItems="start"
          justifyContent="flex-end"
          spacing={0.75}
        >
          <CustomButton
            onClick={handleOpenCreateAvailableDialog}
            color="secondary"
            size="small"
          >
            <Typography variant="body2" fontWeight={500}>
              Agregar
            </Typography>
          </CustomButton>
          <CustomButton
            onClick={handleOpenEditPayoutPlan}
            color="primary"
            size="small"
          >
            <Typography variant="body2" fontWeight={500}>
              Editar
            </Typography>
          </CustomButton>
        </Stack>
      )}
    </div>
  );
};

export default PayoutPlanDetails;
